import { useEffect, useRef, useState } from "react";
import { Crown, Trophy } from "lucide-react";

const CONFETTI_COLORS = ["#FACC15", "#22C55E", "#3B82F6", "#EF4444", "#EC4899", "#FF7A1A"];
const PIECES = Array.from({ length: 56 }, (_, i) => ({ left: (i * 37) % 100, delay: (i % 14) * 0.16, dur: 2.6 + (i % 5) * 0.5, color: CONFETTI_COLORS[i % CONFETTI_COLORS.length] }));

export const Confetti = ({ delay = 1.2, className = "absolute" }) => (
  <div className={`confetti-layer pointer-events-none ${className} inset-0 overflow-hidden z-10`} data-testid="board-confetti">
    {PIECES.map((p, i) => <span key={i} className="confetti-piece" style={{ left: `${p.left}%`, backgroundColor: p.color, animationDelay: `${delay + p.delay}s`, animationDuration: `${p.dur}s` }} />)}
  </div>
);

const RecordBurst = () => (
  <div className="fixed inset-0 z-50 grid place-items-center bg-black/55 backdrop-blur-sm anim-pop" data-testid="class-record-burst">
    <Confetti delay={0} className="fixed" />
    <div className="text-center space-y-[2vh]">
      <Crown className="mx-auto h-[12vh] w-[12vh] text-[#FACC15] anim-bounce drop-shadow-[0_0_30px_rgba(250,204,21,0.8)]" />
      <p className="font-display text-[min(9rem,14vh)] font-bold leading-none neon-text-yellow">YENİ SINIF REKORU!</p>
    </div>
  </div>
);

const useCountUp = (target) => {
  const [shown, setShown] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    fromRef.current = target;
    if (from === target) return undefined;
    const start = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min((now - start) / 1200, 1);
      setShown(Math.round(from + (target - from) * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return shown;
};

const TargetBar = ({ score, target }) => {
  const pct = Math.min(100, Math.round((score / target) * 100));
  const done = score >= target;
  return (
    <div className="mt-[1.2vh] min-w-[18vw]" data-testid="class-target">
      <div className="flex justify-between text-[1.8vh] font-semibold mb-[0.5vh]">
        <span className={done ? "text-[#22C55E]" : "text-slate-300"} data-testid="class-target-left">{done ? "Hedefe ulaşıldı!" : `Hedefe ${target - score} puan kaldı`}</span>
        <span className="text-slate-400">Hedef {target}</span>
      </div>
      <div className="h-[1.6vh] rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full transition-[width] duration-1000 ease-out ${done ? "bg-[#22C55E] shadow-[0_0_14px_rgba(34,197,94,0.7)]" : "bg-gradient-to-r from-[#CA8A04] to-[#FACC15]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const ClassScore = ({ score = 0, best = null, target = 0, compact = false, mini = false }) => {
  const shown = useCountUp(score);
  const prev = useRef(score);
  const [delta, setDelta] = useState(null);
  const [burst, setBurst] = useState(false);
  const beaten = best !== null && best !== undefined && score > best;
  const wasBeaten = useRef(beaten);
  useEffect(() => {
    if (beaten && !wasBeaten.current) {
      setBurst(true);
      const t = setTimeout(() => setBurst(false), 3800);
      wasBeaten.current = true;
      return () => clearTimeout(t);
    }
    wasBeaten.current = beaten;
    return undefined;
  }, [beaten]);
  useEffect(() => {
    const d = score - prev.current;
    prev.current = score;
    if (!d) return undefined;
    setDelta({ d, k: Date.now() });
    const t = setTimeout(() => setDelta(null), 1800);
    return () => clearTimeout(t);
  }, [score]);
  if (mini) {
    return (
      <div className="relative flex items-center gap-[0.8vw] rounded-full border-2 border-[#FACC15]/70 bg-[#0B0F16]/80 px-[1.2vw] py-[0.8vh] shrink-0" data-testid="class-score">
        <Trophy className="h-[2.6vh] w-[2.6vh] text-[#FACC15]" />
        <span className="text-[1.8vh] text-slate-300">Sınıf</span>
        <b key={delta?.k} className={`font-display text-[3.4vh] leading-none tabular-nums text-[#FACC15] ${delta ? "anim-pop" : ""}`} data-testid="class-score-value">{shown}</b>
        {best !== null && best !== undefined && <span className={`text-[1.8vh] font-semibold ${beaten ? "text-[#22C55E]" : "text-slate-400"}`} data-testid="class-score-best">{beaten ? "Rekor kırıldı!" : `Rekor ${best}`}</span>}
        {delta && delta.d > 0 && <span key={`d${delta.k}`} className="absolute -top-[2.4vh] right-[0.6vw] rounded-full bg-[#22C55E] text-[#04210F] px-[0.6vw] font-display font-bold text-[2vh] anim-fade-up" data-testid="class-score-delta">+{delta.d}</span>}
        {burst && <RecordBurst />}
      </div>
    );
  }
  return (
    <div className={`class-score relative overflow-visible rounded-[1.75rem] border-[3px] border-[#FACC15] bg-gradient-to-br from-[#1F2937] to-[#0B0F16] ${compact ? "px-[1.6vw] py-[1.2vh]" : "px-[2vw] py-[2vh]"}`} data-testid="class-score">
      <div className="flex items-center gap-[0.6vw] text-[#FACC15]">
        <Trophy className="h-[2.6vh] w-[2.6vh]" />
        <span className="eyebrow !text-[#FACC15] text-[1.8vh]">Sınıf puanı</span>
      </div>
      <p key={delta?.k} className={`font-display font-bold leading-none tabular-nums neon-text-yellow ${compact ? "text-[min(5rem,8vh)]" : "text-[min(8rem,13vh)]"} ${delta ? "anim-pop" : ""}`} data-testid="class-score-value">{shown}</p>
      {delta && (
        <span key={`d${delta.k}`} className={`absolute -top-[2vh] right-[1vw] rounded-full px-[1vw] py-[0.4vh] font-display font-bold text-[3vh] anim-fade-up ${delta.d > 0 ? "bg-[#22C55E] text-[#04210F]" : "bg-red-500 text-white"}`} data-testid="class-score-delta">
          {delta.d > 0 ? `+${delta.d}` : delta.d}
        </span>
      )}
      {best !== null && best !== undefined && (
        <p className={`mt-[0.6vh] text-[1.9vh] font-semibold ${beaten ? "text-[#22C55E]" : "text-slate-400"}`} data-testid="class-score-best">
          {beaten ? "Rekor kırıldı!" : `Rekor: ${best}`}
        </p>
      )}
      {target > 0 && <TargetBar score={score} target={target} />}
      {burst && <RecordBurst />}
    </div>
  );
};
